/**
 * TOSS Web Application - TanStack Query Client Configuration
 *
 * Centralized QueryClient configuration for server state management.
 * Features:
 * - Optimized default settings for enterprise application
 * - Automatic retry with exponential backoff
 * - Smart refetch on window focus
 * - Error handling integration with API client
 * - Mutation defaults for optimistic updates
 */

import {
  QueryClient,
  QueryClientConfig,
  MutationCache,
  QueryCache,
  type Mutation,
} from '@tanstack/react-query';
import { isAuthError, isForbiddenError, isNetworkError, getErrorMessage } from '@/api/client';
import { useUIStore } from '@/store/uiStore';
import type { DuplicateCheckKeys } from '@/lib/duplicateHint';

// ============================================================================
// Configuration Constants
// ============================================================================

/**
 * Default stale time for queries (5 minutes)
 * Data is considered fresh for this duration and won't trigger refetch
 */
const DEFAULT_STALE_TIME = 1000 * 60 * 5;

/**
 * Default garbage collection time (30 minutes)
 * Unused data is removed from cache after this duration
 */
const DEFAULT_GC_TIME = 1000 * 60 * 30;

/**
 * Maximum retry attempts for failed queries
 */
const MAX_RETRIES = 3;

/**
 * Retry delay calculation (exponential backoff)
 * @param attemptIndex - Current retry attempt (0-indexed)
 * @returns Delay in milliseconds
 */
function getRetryDelay(attemptIndex: number): number {
  // Exponential backoff: 1s, 2s, 4s, capped at 30s
  return Math.min(1000 * Math.pow(2, attemptIndex), 30000);
}

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Determine if a query should retry after an error
 * @param failureCount - Number of failed attempts
 * @param error - The error that occurred
 * @returns Whether to retry the query
 */
function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  // Don't retry if max retries reached
  if (failureCount >= MAX_RETRIES) {
    return false;
  }

  // Don't retry on authentication errors (401)
  // These require user action (re-login)
  if (isAuthError(error)) {
    return false;
  }

  // Don't retry on forbidden errors (403)
  // These are permission-based and won't change with retry
  if (isForbiddenError(error)) {
    return false;
  }

  // Retry on network errors (server might be temporarily unavailable)
  if (isNetworkError(error)) {
    return true;
  }

  // Default: retry other errors (5xx server errors, etc.)
  return true;
}

/**
 * Global error handler for query cache
 * Called when a query fails after all retries
 * @param error - The error that occurred
 */
function handleQueryError(error: unknown): void {
  const message = getErrorMessage(error);

  // Log error in development
  if (import.meta.env.DEV) {
    console.error('[TanStack Query] Query error:', message, error);
  }

  // Note: Global toast notifications should be handled by the component
  // that initiated the query, not here. This is for logging/monitoring.
}

/**
 * Global error handler for mutation cache.
 *
 * Called when ANY mutation fails. Shows a single Russian error toast so that no
 * mutation can ever fail silently. A mutation that already surfaces its own error
 * (inline panel error, or its own `onError` toast) opts out of this global toast
 * by setting `meta: { skipGlobalError: true }` — this prevents double toasts.
 *
 * @param error - The error that occurred
 * @param mutation - The mutation that failed (carries `meta`)
 */
function handleMutationError(
  error: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>
): void {
  const message = getErrorMessage(error);

  // Log error in development
  if (import.meta.env.DEV) {
    console.error('[TanStack Query] Mutation error:', message, error);
  }

  // Let mutations that render their own error UI opt out of the global toast.
  if (mutation.options.meta?.skipGlobalError === true) {
    return;
  }

  // Show a single, user-friendly Russian toast. Accessed imperatively via the
  // store (this runs outside React), matching the per-action `toast.error`
  // pattern used across the app: title "Ошибка" + a specific message.
  useUIStore.getState().showError('Ошибка', message);
}

// ============================================================================
// Cache Configuration
// ============================================================================

/**
 * Query cache with global error handling
 */
const queryCache = new QueryCache({
  onError: handleQueryError,
});

/**
 * Mutation cache with global error handling.
 * Shows a Russian error toast for every failed mutation unless it opted out via
 * `meta: { skipGlobalError: true }`.
 */
const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    handleMutationError(error, mutation);
  },
});

// ============================================================================
// QueryClient Configuration
// ============================================================================

/**
 * QueryClient configuration options
 */
const queryClientConfig: QueryClientConfig = {
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      // Time until data is considered stale
      staleTime: DEFAULT_STALE_TIME,

      // Time until unused data is garbage collected
      gcTime: DEFAULT_GC_TIME,

      // Retry configuration
      retry: shouldRetryQuery,
      retryDelay: getRetryDelay,

      // Refetch behavior
      refetchOnWindowFocus: true, // Refetch when window regains focus
      refetchOnReconnect: true, // Refetch when network reconnects
      refetchOnMount: true, // Refetch when component mounts (if stale)

      // Don't refetch in background (reduces unnecessary API calls)
      refetchInterval: false,

      // Keep previous data while fetching new data
      placeholderData: (previousData: unknown) => previousData,

      // Network mode: always fetch (don't use cache when offline)
      networkMode: 'offlineFirst',

      // Structural sharing for better performance
      structuralSharing: true,
    },
    mutations: {
      // Don't retry mutations by default (they might have side effects)
      retry: false,

      // Network mode for mutations
      networkMode: 'online',
    },
  },
};

// ============================================================================
// QueryClient Instance
// ============================================================================

/**
 * Singleton QueryClient instance
 *
 * Usage:
 * - Import and pass to QueryClientProvider
 * - Use for imperative cache operations (invalidation, prefetching)
 *
 * @example
 * ```tsx
 * // In providers
 * <QueryClientProvider client={queryClient}>
 *   <App />
 * </QueryClientProvider>
 *
 * // Imperative cache invalidation
 * queryClient.invalidateQueries({ queryKey: ['applications'] });
 *
 * // Prefetching
 * await queryClient.prefetchQuery({
 *   queryKey: ['user', userId],
 *   queryFn: () => fetchUser(userId)
 * });
 * ```
 */
export const queryClient = new QueryClient(queryClientConfig);

// ============================================================================
// Query Key Factory
// ============================================================================

/**
 * Query key factory for consistent and type-safe query keys
 *
 * Follows the pattern: ['entity', ...filters/params]
 *
 * @example
 * ```tsx
 * // List all applications
 * useQuery({ queryKey: queryKeys.applications.all })
 *
 * // Get specific application
 * useQuery({ queryKey: queryKeys.applications.detail(id) })
 *
 * // List with filters
 * useQuery({ queryKey: queryKeys.applications.list(filters) })
 * ```
 */
export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    profile: (id: string) => [...queryKeys.users.detail(id), 'profile'] as const,
  },

  // Applications
  applications: {
    all: ['applications'] as const,
    lists: () => [...queryKeys.applications.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.applications.lists(), filters] as const,
    details: () => [...queryKeys.applications.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.applications.details(), id] as const,
    ptos: (applicationId: string) =>
      [...queryKeys.applications.detail(applicationId), 'ptos'] as const,
    pto: (applicationId: string, ptoId: string) =>
      [...queryKeys.applications.ptos(applicationId), ptoId] as const,
    inspections: (applicationId: string, ptoId: string) =>
      [...queryKeys.applications.pto(applicationId, ptoId), 'inspections'] as const,
    /** Additional-work approvals of one ПТО (release Р4). */
    approvals: (applicationId: string, ptoId: string) =>
      [...queryKeys.applications.pto(applicationId, ptoId), 'approvals'] as const,
    /**
     * Inspection redesign, Phase 2 — one `InspectionStateDto`. A full-state write
     * (`place`/`quick`/`impossible`/undo/`complete`) seeds this key with `setQueryData` and does
     * NOT refetch; a fragment write (`units/{id}`, `containers`, the four container-position
     * routes) patches it and THEN invalidates — see `useInspectionFlow.ts` (FE-B).
     */
    inspectionFlow: (applicationId: string, ptoId: string) =>
      [...queryKeys.applications.pto(applicationId, ptoId), 'inspection-flow'] as const,
    /**
     * The auto-works preview, keyed by the container count it was computed FOR — a SIBLING of
     * `inspectionFlow`, not a child, so the `exact: true` invalidations that protect the flow's
     * own cache rule leave it alone and it has to be gone after by prefix.
     */
    inspectionAutoWorks: (applicationId: string, ptoId: string, containersActualCount: number | null) =>
      [...queryKeys.applications.pto(applicationId, ptoId), 'inspection-auto-works', containersActualCount] as const,
    /** Every preview of one visit, whatever count it was keyed by — the prefix to invalidate. */
    inspectionAutoWorksAll: (applicationId: string, ptoId: string) =>
      [...queryKeys.applications.pto(applicationId, ptoId), 'inspection-auto-works'] as const,
    /**
     * The merge comparison (application-merge spec) — a POST read, keyed by the selection (order
     * does not matter) and the requested target (`null` = the server's default). Under `all` on
     * purpose: any application write may change what the window compares. The window disables
     * the query while its merge is in flight, so the merge's own invalidation never re-runs it.
     */
    mergePreview: (applicationIds: readonly string[], targetApplicationId?: string | null) =>
      [
        ...queryKeys.applications.all,
        'merge-preview',
        { applicationIds: [...applicationIds].sort().join(','), targetApplicationId: targetApplicationId ?? null },
      ] as const,
    /**
     * The duplicate hint of the create / edit form (application-merge spec, part 2) — a POST read
     * keyed by the SETTLED key set: sorted lists, the customer and the edited application, so a
     * stale answer for another customer can never land under the current key. Under `all`: any
     * application write may change what matches.
     */
    duplicateCheck: (keys: DuplicateCheckKeys) =>
      [...queryKeys.applications.all, 'duplicate-check', keys] as const,
    /** The card's duplicate notice — a child of `detail`, so it is re-read with the card. */
    duplicates: (applicationId: string) =>
      [...queryKeys.applications.detail(applicationId), 'duplicates'] as const,
    /** One application's `HandoverDto[]`, grouped client-side by `sourceApplicationPtoId`. */
    handovers: (applicationId: string) =>
      [...queryKeys.applications.detail(applicationId), 'handovers'] as const,
    /**
     * Versions (application-versions spec, P4). Children of `detail`, on purpose: every write that
     * invalidates the card also re-reads the versions it just produced and the preview it moved.
     */
    versions: (applicationId: string) =>
      [...queryKeys.applications.detail(applicationId), 'versions'] as const,
    /** The paged list (`useInfiniteQuery`) — a sibling of the per-version keys, not their parent. */
    versionList: (applicationId: string) =>
      [...queryKeys.applications.versions(applicationId), 'list'] as const,
    version: (applicationId: string, versionId: number) =>
      [...queryKeys.applications.versions(applicationId), versionId] as const,
    versionSnapshot: (applicationId: string, versionId: number) =>
      [...queryKeys.applications.version(applicationId, versionId), 'snapshot'] as const,
    versionPreview: (applicationId: string, versionId: number) =>
      [...queryKeys.applications.version(applicationId, versionId), 'restore-preview'] as const,
  },

  // Tasks
  tasks: {
    all: ['tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.tasks.lists(), filters] as const,
    details: () => [...queryKeys.tasks.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tasks.details(), id] as const,
    reports: (taskId: string) =>
      [...queryKeys.tasks.detail(taskId), 'reports'] as const,
  },

  // Expenses
  expenses: {
    all: ['expenses'] as const,
    lists: () => [...queryKeys.expenses.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.expenses.lists(), filters] as const,
    details: () => [...queryKeys.expenses.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.expenses.details(), id] as const,
  },

  // FMO
  fmo: {
    all: ['fmo'] as const,
    ptos: () => [...queryKeys.fmo.all, 'ptos'] as const,
    ptoList: (filters?: Record<string, unknown>) =>
      [...queryKeys.fmo.ptos(), filters] as const,
    pto: (ptoId: string) => [...queryKeys.fmo.ptos(), ptoId] as const,
    payments: () => [...queryKeys.fmo.all, 'payments'] as const,
    paymentList: (filters?: Record<string, unknown>) =>
      [...queryKeys.fmo.payments(), filters] as const,
  },

  // Directory - Customers
  customers: {
    all: ['customers'] as const,
    lists: () => [...queryKeys.customers.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.customers.lists(), filters] as const,
    short: () => [...queryKeys.customers.all, 'short'] as const,
    details: () => [...queryKeys.customers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.customers.details(), id] as const,
    contacts: (customerId: string) =>
      [...queryKeys.customers.detail(customerId), 'contacts'] as const,
  },

  // Directory - Containers
  containers: {
    all: ['containers'] as const,
    lists: () => [...queryKeys.containers.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.containers.lists(), filters] as const,
    details: () => [...queryKeys.containers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.containers.details(), id] as const,
    inspections: (id: string) => [...queryKeys.containers.detail(id), 'inspections'] as const,
  },

  // Directory - Equipment
  equipment: {
    all: ['equipment'] as const,
    lists: () => [...queryKeys.equipment.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.equipment.lists(), filters] as const,
    details: () => [...queryKeys.equipment.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.equipment.details(), id] as const,
    inspections: (id: string) => [...queryKeys.equipment.detail(id), 'inspections'] as const,
  },

  // Directory - PTO
  pto: {
    all: ['pto'] as const,
    lists: () => [...queryKeys.pto.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.pto.lists(), filters] as const,
    short: () => [...queryKeys.pto.all, 'short'] as const,
    details: () => [...queryKeys.pto.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.pto.details(), id] as const,
  },

  // Directory - Services
  services: {
    all: ['services'] as const,
    lists: () => [...queryKeys.services.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.services.lists(), filters] as const,
    details: () => [...queryKeys.services.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.services.details(), id] as const,
  },

  // Directory - Directions
  directions: {
    all: ['directions'] as const,
    lists: () => [...queryKeys.directions.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.directions.lists(), filters] as const,
    short: () => [...queryKeys.directions.all, 'short'] as const,
    details: () => [...queryKeys.directions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.directions.details(), id] as const,
  },

  // Directory - Materials
  materials: {
    all: ['materials'] as const,
    lists: () => [...queryKeys.materials.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.materials.lists(), filters] as const,
    details: () => [...queryKeys.materials.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.materials.details(), id] as const,
  },

  // Directory - Instructions
  instructions: {
    all: ['instructions'] as const,
    lists: () => [...queryKeys.instructions.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.instructions.lists(), filters] as const,
    details: () => [...queryKeys.instructions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.instructions.details(), id] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.notifications.lists(), filters] as const,
    unreadCount: () => [...queryKeys.notifications.all, 'unread-count'] as const,
  },

  // Messenger
  messenger: {
    all: ['messenger'] as const,
    chats: () => [...queryKeys.messenger.all, 'chats'] as const,
    chatList: (filters?: Record<string, unknown>) =>
      [...queryKeys.messenger.chats(), filters] as const,
    chat: (chatId: string) => [...queryKeys.messenger.chats(), chatId] as const,
    messages: (chatId: string) =>
      [...queryKeys.messenger.chat(chatId), 'messages'] as const,
    search: (query: string, chatId?: string) =>
      [...queryKeys.messenger.all, 'search', query, chatId] as const,
    searchInfinite: (query: string, chatId?: string, pageSize?: number) =>
      [...queryKeys.messenger.all, 'search', 'infinite', query, chatId, pageSize] as const,
    unreadCount: () => [...queryKeys.messenger.all, 'unread-count'] as const,
    onlineUsers: () => [...queryKeys.messenger.all, 'online-users'] as const,
    /**
     * Кандидаты в участники чата со стороны компании-заказчика.
     *
     * Под `messenger.all`, а НЕ под ключами справочника заказчиков: инвалидация состава чата не
     * должна дёргать таблицу учётных записей в карточке клиента, и наоборот. Это два разных
     * экрана, живущих за двумя разными правами.
     */
    customerChatCandidates: (customerId: string) =>
      [...queryKeys.messenger.all, 'customer-chat-candidates', customerId] as const,
  },

  // Audit Logs
  auditLogs: {
    all: ['auditLogs'] as const,
    lists: () => [...queryKeys.auditLogs.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.auditLogs.lists(), filters] as const,
    details: () => [...queryKeys.auditLogs.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.auditLogs.details(), id] as const,
  },

  // Vision v4 Phase 1 — Stations directory
  stations: {
    all: ['stations'] as const,
    lists: () => [...queryKeys.stations.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.stations.lists(), filters] as const,
    details: () => [...queryKeys.stations.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.stations.details(), id] as const,
    byPto: (ptoId: string) => [...queryKeys.stations.all, 'by-pto', ptoId] as const,
  },

  // Vision v4 Phase 1 — Terminals directory
  terminals: {
    all: ['terminals'] as const,
    lists: () => [...queryKeys.terminals.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.terminals.lists(), filters] as const,
    details: () => [...queryKeys.terminals.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.terminals.details(), id] as const,
    byPto: (ptoId: string) => [...queryKeys.terminals.all, 'by-pto', ptoId] as const,
  },

  // Vision v4 Phase 2 — Pricing
  pricing: {
    all: ['pricing'] as const,
    servicePto: (params?: Record<string, unknown>) =>
      [...queryKeys.pricing.all, 'service-pto', params] as const,
    customerServicePto: (params?: Record<string, unknown>) =>
      [...queryKeys.pricing.all, 'customer-service-pto', params] as const,
    customerPrices: (customerId: string) =>
      [...queryKeys.pricing.all, 'customer-service-pto', 'by-customer', customerId] as const,
    customerMileageRates: (customerId: string) =>
      [...queryKeys.pricing.all, 'customer-mileage-rates', customerId] as const,
    customerNormHourRates: (customerId: string) =>
      [...queryKeys.pricing.all, 'customer-normhour-rates', customerId] as const,
  },

  // Inspection redesign, Phase 2 — container-attribute directories (types + sizes)
  containerTypes: {
    all: ['containerTypes'] as const,
    lists: () => [...queryKeys.containerTypes.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.containerTypes.lists(), filters] as const,
    details: () => [...queryKeys.containerTypes.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.containerTypes.details(), id] as const,
  },
  containerSizes: {
    all: ['containerSizes'] as const,
    lists: () => [...queryKeys.containerSizes.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.containerSizes.lists(), filters] as const,
    details: () => [...queryKeys.containerSizes.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.containerSizes.details(), id] as const,
  },

  // Inspection redesign, Phase 2 — inspection-reasons directory
  inspectionReasons: {
    all: ['inspectionReasons'] as const,
    lists: () => [...queryKeys.inspectionReasons.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.inspectionReasons.lists(), filters] as const,
    details: () => [...queryKeys.inspectionReasons.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.inspectionReasons.details(), id] as const,
  },

  // PTO crew, Phase 4 — mechanic-types directory. No `detail` — the API has no GET by id.
  mechanicTypes: {
    all: ['mechanicTypes'] as const,
    lists: () => [...queryKeys.mechanicTypes.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.mechanicTypes.lists(), filters] as const,
  },

  // Vision v4 Phase 2 — Available services (filtered by PTO flags + exclusivity)
  availableServices: {
    all: ['availableServices'] as const,
    forPto: (ptoId: string, applicationPtoId: string, customerId?: string) =>
      [...queryKeys.availableServices.all, ptoId, applicationPtoId, customerId] as const,
  },

  // Vision v4 Phase 5 — Payroll
  payroll: {
    all: ['payroll'] as const,
    summary: (filters?: Record<string, unknown>) =>
      [...queryKeys.payroll.all, 'summary', filters] as const,
    summaryInfinite: (filters?: Record<string, unknown>) =>
      [...queryKeys.payroll.all, 'summary-infinite', filters] as const,
  },

  // Admin — application settings (PDF document header, etc.)
  settings: {
    all: ['settings'] as const,
    documentHeader: () => [...queryKeys.settings.all, 'document-header'] as const,
  },

  // Files — batch metadata for mixed image/video galleries (video release)
  files: {
    all: ['files'] as const,
    /** `ids` sorted by the caller so the same set hits one cache entry regardless of order. */
    meta: (ids: string[]) => [...queryKeys.files.all, 'meta', ids] as const,
  },
} as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Invalidate all queries for a specific entity
 * @param entity - Entity key from queryKeys
 *
 * @example
 * ```tsx
 * // After creating a new application
 * invalidateEntity('applications');
 * ```
 */
export function invalidateEntity(
  entity: keyof typeof queryKeys
): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: queryKeys[entity].all,
  });
}

/** True when any segment of a query key is `id`, or an object segment holds it as a value. */
function keyMentions(queryKey: readonly unknown[], id: string): boolean {
  return queryKey.some(
    (part) =>
      part === id ||
      (typeof part === 'object' && part !== null && Object.values(part).includes(id))
  );
}

/** `id` strings of the objects in `items`, when it is an array of objects that carry one. */
function idsOf(items: unknown, field: 'id' | 'ptoId'): string[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => (typeof item === 'object' && item !== null ? (item as Record<string, unknown>)[field] : undefined))
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/**
 * The application-PTO ids the cache knows for one application, read BEFORE anything refetches: the
 * card's `applicationPTOs` (both detail keys) and the PTO of every change group a cached version page
 * or restore preview holds — the latter also names the PTOs a rollback deletes or re-creates.
 */
function cachedPtoIdsOf(client: QueryClient, applicationId: string): Set<string> {
  const ids = new Set<string>();
  for (const key of [queryKeys.applications.detail(applicationId), ['application', applicationId]]) {
    const data = client.getQueryData<{ applicationPTOs?: unknown }>(key);
    idsOf(data?.applicationPTOs, 'id').forEach((id) => ids.add(id));
  }
  for (const [, data] of client.getQueriesData<{ groups?: unknown }>({
    queryKey: queryKeys.applications.versions(applicationId),
  })) {
    idsOf(data?.groups, 'ptoId').forEach((id) => ids.add(id));
  }
  return ids;
}

/**
 * Invalidate every query that can hold this application's data (application-versions spec, P4).
 *
 * A rollback rewrites the whole application at once — header, PTOs, units, inspections, crew —
 * and the keys that cache those pieces are spread over several roots: `applications.*`, the
 * history timeline's `['application-history', id]`, and per-feature keys (`pto`, `fmo`,
 * `handovers`…) that carry the id as one of their segments. Naming them one by one would miss
 * the next key someone adds, so the last pass is a predicate over the id itself.
 *
 * Some keys carry only a PTO's id — `fmo.pto(ptoId)`, its documents, the available-services lookup
 * (gate m7). Those are matched by the application's PTO ids the cache already holds
 * ({@link cachedPtoIdsOf}); FMO's PTO lists, keyed by filters alone, go by their root.
 */
export function invalidateApplicationEverywhere(
  client: QueryClient,
  applicationId: string
): Promise<void> {
  const ptoIds = [...cachedPtoIdsOf(client, applicationId)];
  return Promise.all([
    client.invalidateQueries({ queryKey: queryKeys.applications.all }),
    client.invalidateQueries({ queryKey: ['application-history', applicationId] }),
    client.invalidateQueries({ queryKey: queryKeys.fmo.ptos() }),
    client.invalidateQueries({
      predicate: (query) =>
        keyMentions(query.queryKey, applicationId) ||
        ptoIds.some((ptoId) => keyMentions(query.queryKey, ptoId)),
    }),
  ]).then(() => undefined);
}

/**
 * Clear all cached data
 * Useful when user logs out
 */
export function clearQueryCache(): void {
  queryClient.clear();
}

/**
 * Reset all queries to their initial state
 * Useful when user changes or on critical errors
 */
export function resetQueryClient(): void {
  queryClient.resetQueries();
}

// ============================================================================
// DevTools Support
// ============================================================================

/**
 * Check if React Query DevTools should be enabled
 * Only in development mode
 */
export const isDevToolsEnabled = import.meta.env.DEV;

export default queryClient;
